import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { useDashboard } from '../../hooks/useDashboard'
import { useAuth } from '../../hooks/useAuth'
import { Loading } from '../common/Loading'
import { Bot, MessageSquare, MessageCircle, Users, Settings, TrendingUp, Activity, RefreshCw, LogOut, Bell, Clock, Crown, DollarSign } from 'lucide-react'
import { logger } from '../../utils/logger'

/** Conversas com status humano e sem users(id) em attendant_id — fila para assumir atendimento. */
function formatConversationsWaitingForAttendant(count: number): string {
  if (count <= 0) return 'Nenhuma conversa na fila'
  if (count === 1) return '1 conversa na fila'
  return `${count} conversas na fila`
}

const PersonalizedDashboard: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  const { stats, activities, botsStatus, isLoading, error, refreshData } = useDashboard()
  const { logout } = useAuth()

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      logger.error('Erro ao fazer logout:', error)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'bg-green-500'
      case 'authenticating':
        return 'bg-yellow-500'
      case 'failed':
        return 'bg-red-500'
      default:
        return 'bg-gray-500'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'connected':
        return 'Conectado'
      case 'authenticating':
        return 'Autenticando'
      case 'failed':
        return 'Falhou'
      default:
        return 'Desconectado'
    }
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'bot_connected':
      case 'bot_disconnected':
        return Activity
      case 'new_conversation':
        return MessageSquare
      case 'template_created':
      case 'menu_created':
        return Settings
      default:
        return Activity
    }
  }

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'bot_connected':
        return 'text-green-500'
      case 'bot_disconnected':
        return 'text-red-500'
      case 'new_conversation':
        return 'text-blue-500'
      case 'template_created':
      case 'menu_created':
        return 'text-purple-500'
      default:
        return 'text-gray-500'
    }
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 60) {
      return `${minutes} minutos atrás`
    } else if (hours < 24) {
      return `${hours} horas atrás`
    } else {
      return `${days} dias atrás`
    }
  }

  // Determinar tipo de usuário e permissões
  const getUserType = () => {
    if (user?.type === 'user') {
      return 'attendant' // users(id) - atendentes
    } else if (user?.type === 'admin' && user?.status === 'super') {
      return 'super_admin' // admins(id) com status = "super"
    } else if (user?.type === 'admin' && user?.hasActiveSubscription) {
      return 'admin_with_subscription' // admins(id) com admin_subscriptions
    }
    return 'admin_with_subscription' // fallback
  }

  const userType = getUserType()

  // Definir menus baseado no tipo de usuário
  const getAvailableMenus = () => {
    const baseMenus = [
      { 
        name: 'Ver Conversas', 
        path: '/conversations', 
        icon: MessageSquare, 
        color: 'hover:border-purple-300', 
        iconColor: 'text-purple-500',
        iconBg: 'bg-purple-100 group-hover:bg-purple-200',
        textColor: 'group-hover:text-purple-600',
        description: 'Acompanhe as conversas ativas' 
      },
      { 
        name: 'Mensagens rápidas', 
        path: '/quick-replies', 
        icon: MessageCircle, 
        color: 'hover:border-indigo-300', 
        iconColor: 'text-indigo-500',
        iconBg: 'bg-indigo-100 group-hover:bg-indigo-200',
        textColor: 'group-hover:text-indigo-600',
        description: 'Cadastre e gerencie suas mensagens rápidas' 
      },
      { 
        name: 'Clientes', 
        path: '/clients', 
        icon: Users, 
        color: 'hover:border-amber-300', 
        iconColor: 'text-amber-500',
        iconBg: 'bg-amber-100 group-hover:bg-amber-200',
        textColor: 'group-hover:text-amber-600',
        description: 'Consulte e edite dados dos clientes' 
      },
      { 
        name: 'Financeiro', 
        path: '/financial', 
        icon: DollarSign, 
        color: 'hover:border-green-300', 
        iconColor: 'text-green-500',
        iconBg: 'bg-green-100 group-hover:bg-green-200',
        textColor: 'group-hover:text-green-600',
        description: 'Gerencie faturas e pagamentos' 
      }
    ]

    if (userType === 'attendant') {
      // users(id) - Ver Conversas, Mensagens rápidas e Financeiro
      return baseMenus
    } else if (userType === 'super_admin') {
      // admins(id) com status = "super" - todos os menus
      return [
        { 
          name: 'Criar Novo Bot', 
          path: '/bots', 
          icon: Bot, 
          color: 'hover:border-blue-300', 
          iconColor: 'text-blue-500',
          iconBg: 'bg-blue-100 group-hover:bg-blue-200',
          textColor: 'group-hover:text-blue-600',
          description: 'Configure um novo chatbot' 
        },
        { 
          name: 'Criar Menus', 
          path: '/menus/create', 
          icon: MessageSquare, 
          color: 'hover:border-green-300', 
          iconColor: 'text-green-500',
          iconBg: 'bg-green-100 group-hover:bg-green-200',
          textColor: 'group-hover:text-green-600',
          description: 'Configure menus e submenus' 
        },
        { 
          name: 'Gerenciar Templates', 
          path: '/templates/management', 
          icon: Settings, 
          color: 'hover:border-orange-300', 
          iconColor: 'text-orange-500',
          iconBg: 'bg-orange-100 group-hover:bg-orange-200',
          textColor: 'group-hover:text-orange-600',
          description: 'Crie templates personalizados' 
        },
        { 
          name: 'Ver Conversas', 
          path: '/conversations', 
          icon: MessageSquare, 
          color: 'hover:border-purple-300', 
          iconColor: 'text-purple-500',
          iconBg: 'bg-purple-100 group-hover:bg-purple-200',
          textColor: 'group-hover:text-purple-600',
          description: 'Acompanhe as conversas ativas' 
        },
        { 
          name: 'Clientes', 
          path: '/clients', 
          icon: Users, 
          color: 'hover:border-amber-300', 
          iconColor: 'text-amber-500',
          iconBg: 'bg-amber-100 group-hover:bg-amber-200',
          textColor: 'group-hover:text-amber-600',
          description: 'Consulte e edite dados dos clientes' 
        },
        { 
          name: 'Gerenciar Admins', 
          path: '/admin-management', 
          icon: Crown, 
          color: 'hover:border-yellow-300', 
          iconColor: 'text-yellow-500',
          iconBg: 'bg-yellow-100 group-hover:bg-yellow-200',
          textColor: 'group-hover:text-yellow-600',
          description: 'Gerencie administradores e assinaturas' 
        },
        { 
          name: 'Mensagens do Bot', 
          path: '/bot-messages', 
          icon: MessageSquare, 
          color: 'hover:border-indigo-300', 
          iconColor: 'text-indigo-500',
          iconBg: 'bg-indigo-100 group-hover:bg-indigo-200',
          textColor: 'group-hover:text-indigo-600',
          description: 'Personalize mensagens automáticas' 
        },
        { 
          name: 'Planos de Assinatura', 
          path: '/subscription-plans', 
          icon: Settings, 
          color: 'hover:border-indigo-300', 
          iconColor: 'text-indigo-500',
          iconBg: 'bg-indigo-100 group-hover:bg-indigo-200',
          textColor: 'group-hover:text-indigo-600',
          description: 'Configure planos e limites' 
        },
        { 
          name: 'Atendentes', 
          path: '/user-management', 
          icon: Users, 
          color: 'hover:border-green-300', 
          iconColor: 'text-green-500',
          iconBg: 'bg-green-100 group-hover:bg-green-200',
          textColor: 'group-hover:text-green-600',
          description: 'Gerencie usuários de atendimento' 
        },
        { 
          name: 'Financeiro', 
          path: '/financial', 
          icon: DollarSign, 
          color: 'hover:border-green-300', 
          iconColor: 'text-green-500',
          iconBg: 'bg-green-100 group-hover:bg-green-200',
          textColor: 'group-hover:text-green-600',
          description: 'Gerencie faturas e pagamentos' 
        },
        { 
          name: 'Configurações', 
          path: '/settings', 
          icon: Settings, 
          color: 'hover:border-gray-300', 
          iconColor: 'text-gray-500',
          iconBg: 'bg-gray-100 group-hover:bg-gray-200',
          textColor: 'group-hover:text-gray-600',
          description: 'Gerencie suas configurações' 
        }
      ]
    } else if (userType === 'admin_with_subscription') {
      // admins(id) com admin_subscriptions - todos exceto Gerenciar Admins
      return [
        { 
          name: 'Criar Novo Bot', 
          path: '/bots', 
          icon: Bot, 
          color: 'hover:border-blue-300', 
          iconColor: 'text-blue-500',
          iconBg: 'bg-blue-100 group-hover:bg-blue-200',
          textColor: 'group-hover:text-blue-600',
          description: 'Configure um novo chatbot' 
        },
        { 
          name: 'Criar Menus', 
          path: '/menus/create', 
          icon: MessageSquare, 
          color: 'hover:border-green-300', 
          iconColor: 'text-green-500',
          iconBg: 'bg-green-100 group-hover:bg-green-200',
          textColor: 'group-hover:text-green-600',
          description: 'Configure menus e submenus' 
        },
        { 
          name: 'Gerenciar Templates', 
          path: '/templates/management', 
          icon: Settings, 
          color: 'hover:border-orange-300', 
          iconColor: 'text-orange-500',
          iconBg: 'bg-orange-100 group-hover:bg-orange-200',
          textColor: 'group-hover:text-orange-600',
          description: 'Crie templates personalizados' 
        },
        { 
          name: 'Ver Conversas', 
          path: '/conversations', 
          icon: MessageSquare, 
          color: 'hover:border-purple-300', 
          iconColor: 'text-purple-500',
          iconBg: 'bg-purple-100 group-hover:bg-purple-200',
          textColor: 'group-hover:text-purple-600',
          description: 'Acompanhe as conversas ativas' 
        },
        { 
          name: 'Clientes', 
          path: '/clients', 
          icon: Users, 
          color: 'hover:border-amber-300', 
          iconColor: 'text-amber-500',
          iconBg: 'bg-amber-100 group-hover:bg-amber-200',
          textColor: 'group-hover:text-amber-600',
          description: 'Consulte e edite dados dos clientes' 
        },
        { 
          name: 'Mensagens do Bot', 
          path: '/bot-messages', 
          icon: MessageSquare, 
          color: 'hover:border-indigo-300', 
          iconColor: 'text-indigo-500',
          iconBg: 'bg-indigo-100 group-hover:bg-indigo-200',
          textColor: 'group-hover:text-indigo-600',
          description: 'Personalize mensagens automáticas' 
        },
        { 
          name: 'Atendentes', 
          path: '/user-management', 
          icon: Users, 
          color: 'hover:border-green-300', 
          iconColor: 'text-green-500',
          iconBg: 'bg-green-100 group-hover:bg-green-200',
          textColor: 'group-hover:text-green-600',
          description: 'Gerencie usuários de atendimento' 
        },
        { 
          name: 'Financeiro', 
          path: '/financial', 
          icon: DollarSign, 
          color: 'hover:border-green-300', 
          iconColor: 'text-green-500',
          iconBg: 'bg-green-100 group-hover:bg-green-200',
          textColor: 'group-hover:text-green-600',
          description: 'Gerencie faturas e pagamentos' 
        },
        { 
          name: 'Configurações', 
          path: '/settings', 
          icon: Settings, 
          color: 'hover:border-gray-300', 
          iconColor: 'text-gray-500',
          iconBg: 'bg-gray-100 group-hover:bg-gray-200',
          textColor: 'group-hover:text-gray-600',
          description: 'Gerencie suas configurações' 
        }
      ]
    }

    return baseMenus
  }

  const availableMenus = getAvailableMenus()

  if (isLoading) {
    return <Loading fullScreen />
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Erro ao carregar dashboard</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={refreshData}
            className="btn btn-primary"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Tentar Novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Olá, {user?.name}! 👋
            </h1>
            <p className="text-gray-600 mt-2">
              Aqui está um resumo do seu sistema de chatbots
            </p>
            <div className="flex items-center mt-2 text-sm text-gray-500">
              <Clock className="h-4 w-4 mr-1" />
              {new Date().toLocaleDateString('pt-BR', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={refreshData}
              className="btn btn-outline btn-md"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </button>
            <button
              onClick={handleLogout}
              className="btn btn-md btn-outline btn-red"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sair
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Bots Ativos</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.bots?.connected || 0}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {stats?.bots?.total || 0} total
              </p>
            </div>
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-3">
              <Bot className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Conversas Hoje</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.conversations?.total || 0}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {formatConversationsWaitingForAttendant(stats?.conversations?.waiting_human ?? 0)}
              </p>
            </div>
            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-3">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Clientes</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.clients?.total || 0}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                cadastrados no sistema
              </p>
            </div>
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-3">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Taxa de Resposta</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.responseRate || 0}%
              </p>
              <p className="text-sm text-gray-500 mt-1">
                respostas automáticas hoje
              </p>
            </div>
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-3">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Atividades Recentes */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Atividades Recentes</h2>
              <div className="flex items-center text-sm text-gray-500">
                <Bell className="h-4 w-4 mr-1" />
                {activities.length} atividades
              </div>
            </div>
          </div>
          <div className="p-6">
            {activities.length > 0 ? (
              <div className="space-y-4">
                {activities.slice(0, 5).map((activity) => {
                  const Icon = getActivityIcon(activity.type)
                  return (
                    <div key={activity.id} className="flex items-start space-x-3">
                      <div className={`${getActivityColor(activity.type)} p-2 rounded-full`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{activity.message}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatTime(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Nenhuma atividade recente</p>
              </div>
            )}
          </div>
        </div>

        {/* Status dos Bots */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Status dos Bots</h2>
              <div className="flex items-center text-sm text-gray-500">
                <Activity className="h-4 w-4 mr-1" />
                {stats?.bots?.connected || 0} conectados
              </div>
            </div>
          </div>
          <div className="p-6">
            {botsStatus.length > 0 ? (
              <div className="space-y-4">
                {botsStatus.map((bot) => {
                  const waitingForAttendant =
                    stats?.bots_waiting_conversations?.find((b) => b.id === bot.id)?.waiting_conversations ?? 0
                  return (
                  <div key={bot.id} className="flex items-center justify-between p-4 rounded-lg border">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 ${getStatusColor(bot.status)} rounded-full`}></div>
                      <div>
                        <p className="font-medium text-gray-900">{bot.name}</p>
                        <p className="text-sm text-gray-600">
                          {getStatusText(bot.status)}
                        </p>
                        {bot.lastActivity && (
                          <p className="text-xs text-gray-500">
                            Última atividade: {formatTime(bot.lastActivity)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-sm font-medium ${
                        bot.status === 'connected' ? 'text-green-600' :
                        bot.status === 'authenticating' ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {getStatusText(bot.status)}
                      </span>
                      <p className="text-xs text-gray-500">
                        {formatConversationsWaitingForAttendant(waitingForAttendant)}
                      </p>
                    </div>
                  </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Bot className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                {user?.type === 'user' ? (
                  <>
                    <p className="text-gray-700 font-medium mb-1">Você não está associado a nenhum bot</p>
                    <p className="text-gray-500 text-sm">
                      Para efetuar atendimento, é necessário que um administrador vincule seu usuário a um ou mais bots em &quot;Permissões por bot&quot;.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-gray-500 mb-4">Nenhum bot encontrado</p>
                    <button
                      onClick={() => navigate('/bots')}
                      className="btn btn-primary btn-sm"
                    >
                      Criar Primeiro Bot
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Ações Rápidas */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Ações Rápidas</h2>
          <div className="text-sm text-gray-500">
            Acesso rápido às principais funcionalidades
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {availableMenus.map((menu, index) => {
            const IconComponent = menu.icon
            return (
              <button
                key={index}
                onClick={() => navigate(menu.path)}
                className={`p-6 bg-white rounded-lg shadow-sm border ${menu.color} transition-all duration-200 text-left group`}
              >
                <div className={`${menu.iconBg} rounded-lg p-3 w-fit mb-3 transition-colors duration-200`}>
                  <IconComponent className={`w-8 h-8 ${menu.iconColor}`} />
                </div>
                <h3 className={`font-medium text-gray-900 ${menu.textColor} transition-colors duration-200`}>
                  {menu.name}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {menu.description}
                </p>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default PersonalizedDashboard



